/**
 * The manual's motion that needs a script: the rail highlight's spring, the
 * reveal of captures and tables, and the fade when the chapter changes.
 * Press feedback and the Contents sheet are CSS (`theme/motion.css`).
 *
 * Ported lightly from the site's design (Apple v3): the same spring — a
 * damping ratio and a response, interruptible, always starting from where the
 * thing is on screen — but only where it earns its place. Under
 * `prefers-reduced-motion` every one of these places the thing where it ends
 * and stops.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import type React from 'react';

/** True when the reader asked the system for less motion. Read each time: it can change under a page. */
export function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** One sprung value: where it is, where it is going, how fast. */
interface Spring {
  v: number;
  t: number;
  vel: number;
}

/**
 * One frame of a damped spring, in small fixed steps so a long frame does not
 * overshoot. `d` is the damping ratio (1: no bounce), `r` the response in
 * seconds — the time a swing takes.
 */
function step(p: Spring, dt: number, d: number, r: number): void {
  const w = (2 * Math.PI) / r;
  const n = Math.max(1, Math.ceil(dt / 0.004));
  const h = dt / n;
  for (let i = 0; i < n; i++) {
    const a = -w * w * (p.v - p.t) - 2 * d * w * p.vel;
    p.vel += a * h;
    p.v += p.vel * h;
  }
}

/**
 * The rail's soft pill, sprung to the chapter being read.
 *
 * `rail` holds the links, `pill` is the highlight inside it. The pill is
 * placed under the `aria-current` link without motion the first time, on a
 * resize and under reduced motion; a chapter change after that springs it there
 * (critically damped, 0.38 s) — position and height together, since a title
 * that wraps makes a taller row.
 */
export function useRailHighlight(
  rail: React.RefObject<HTMLElement | null>,
  pill: React.RefObject<HTMLElement | null>,
  current: string
): void {
  const state = useRef<{ y: Spring; h: Spring; frame: number; placed: boolean } | null>(null);

  useLayoutEffect(() => {
    const box = rail.current;
    const hl = pill.current;
    if (!box || !hl) return;
    const s = (state.current ??= {
      y: { v: 0, t: 0, vel: 0 },
      h: { v: 32, t: 32, vel: 0 },
      frame: 0,
      placed: false
    });

    const paint = (): void => {
      hl.style.transform = `translate3d(0, ${s.y.v.toFixed(2)}px, 0)`;
      hl.style.height = `${s.h.v.toFixed(2)}px`;
    };

    const target = (): { y: number; h: number } | null => {
      const link = box.querySelector<HTMLElement>('[aria-current="page"]');
      if (!link) return null;
      return { y: link.getBoundingClientRect().top - box.getBoundingClientRect().top, h: link.offsetHeight };
    };

    const snap = (): void => {
      const at = target();
      if (!at) {
        hl.style.opacity = '0';
        return;
      }
      window.cancelAnimationFrame(s.frame);
      s.y = { v: at.y, t: at.y, vel: 0 };
      s.h = { v: at.h, t: at.h, vel: 0 };
      s.placed = true;
      paint();
      hl.style.opacity = '1';
    };

    const springTo = (): void => {
      const at = target();
      if (!at || !s.placed || reducedMotion()) {
        snap();
        return;
      }
      s.y.t = at.y;
      s.h.t = at.h;
      hl.style.opacity = '1';
      window.cancelAnimationFrame(s.frame);
      let last = performance.now();
      const frame = (now: number): void => {
        const dt = Math.min(0.064, Math.max(0, (now - last) / 1000));
        last = now;
        step(s.y, dt, 1, 0.38);
        step(s.h, dt, 1, 0.38);
        const settled =
          Math.abs(s.y.v - s.y.t) < 0.05 && Math.abs(s.y.vel) < 0.6 && Math.abs(s.h.v - s.h.t) < 0.05;
        if (settled) {
          s.y = { v: s.y.t, t: s.y.t, vel: 0 };
          s.h = { v: s.h.t, t: s.h.t, vel: 0 };
        }
        paint();
        if (!settled) s.frame = window.requestAnimationFrame(frame);
      };
      s.frame = window.requestAnimationFrame(frame);
    };

    springTo();
    return () => window.cancelAnimationFrame(s.frame);
  }, [rail, pill, current]);

  // A resize, or the fonts arriving, moves the rows under the pill: follow them without motion.
  useEffect(() => {
    const box = rail.current;
    const hl = pill.current;
    if (!box || !hl) return;
    const follow = (): void => {
      const link = box.querySelector<HTMLElement>('[aria-current="page"]');
      const s = state.current;
      if (!link || !s || !s.placed) return;
      window.cancelAnimationFrame(s.frame);
      const y = link.getBoundingClientRect().top - box.getBoundingClientRect().top;
      s.y = { v: y, t: y, vel: 0 };
      s.h = { v: link.offsetHeight, t: link.offsetHeight, vel: 0 };
      hl.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      hl.style.height = `${link.offsetHeight}px`;
    };
    const observer = new ResizeObserver(follow);
    observer.observe(box);
    return () => observer.disconnect();
  }, [rail, pill]);
}

/**
 * Captures and tables arrive once, when they are reached.
 *
 * Only what starts below the fold is held back — anything on screen when the
 * chapter opens (or when a deep link lands on it) is never hidden. React owns
 * the prose's HTML, so the classes are set again for each chapter, and a
 * class lost to a re-render only means the thing is shown.
 */
export function useReveal(root: React.RefObject<HTMLElement | null>, html: string): void {
  useEffect(() => {
    const box = root.current;
    if (!box || reducedMotion() || typeof IntersectionObserver === 'undefined') return;

    const fold = window.innerHeight * 0.92;
    const held = [...box.querySelectorAll<HTMLElement>('.al-shot, .al-table')].filter(
      (el) => el.getBoundingClientRect().top > fold
    );
    if (held.length === 0) return;

    let batch = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          observer.unobserve(el);
          // Two reached in one scroll arrive 60 ms apart, not as one block.
          el.style.transitionDelay = `${(batch++ % 4) * 60}ms`;
          el.classList.add('is-in');
          window.setTimeout(() => {
            el.style.transitionDelay = '';
            batch = 0;
          }, 700);
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    for (const el of held) {
      el.classList.add('al-rv');
      observer.observe(el);
    }
    return () => {
      observer.disconnect();
      for (const el of held) {
        el.classList.remove('al-rv', 'is-in');
        el.style.transitionDelay = '';
      }
    };
  }, [root, html]);
}

/**
 * A short fade as one chapter gives way to the next: the new one comes up
 * from 8px below, in 280 ms. Not on the first chapter of a visit, and not at
 * all under reduced motion.
 */
export function useChapterFade(article: React.RefObject<HTMLElement | null>, id: string): void {
  // The chapter last shown, rather than a « first time » flag: an effect run
  // twice for the same chapter (React's strict mode does) must not fade it.
  const shown = useRef(id);
  useLayoutEffect(() => {
    if (shown.current === id) return;
    shown.current = id;
    const el = article.current;
    if (!el || reducedMotion() || typeof el.animate !== 'function') return;
    const fade = el.animate(
      [
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'none' }
      ],
      { duration: 280, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
    );
    return () => fade.cancel();
  }, [article, id]);
}
